/**
 * TSC Product Catalog block — filter console driver.
 *
 * One instance per [data-txpcat] block (blocks/product-catalog/). The
 * console's own controls hold the filter state — option checkboxes,
 * the two range inputs (indices into the psi stops), the search box,
 * the sort menu. Every change repaints the console at once (summaries,
 * badges, chips, presets) and refetches the results from
 * tx-sensors/v1/catalog, which answers with the cards + pager HTML, the
 * results region's name and the option counts to repaint. The signed
 * `cfg` token carries the editor's scope and filters; only the visitor's
 * state is sent beside it.
 *
 * The page URL is never rewritten — the block sits inside an arbitrary
 * page (same rule as the Product Grid block). The state is kept in
 * history.state instead, so Back (or a reload) restores it; the
 * controls sit in a <form autocomplete="off"> so the browser's own form
 * restoration can't tick boxes behind the script's back.
 *
 * No-JS: the server-rendered first page stays; pcat.scss hides the
 * controls that would do nothing.
 */
const SEARCH_DELAY = 300;
const RANGE_DELAY = 200;
const FETCH_TIMEOUT = 15000;

const psi = (v) => Number(v).toLocaleString('en-US');
const kpsi = (v) => (v >= 1000 ? `${+(v / 1000).toFixed(2)}K` : String(v));
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const coarsePointer = () => window.matchMedia('(pointer: coarse)').matches;

// sprintf for the translated strings: %s / %d, or positional %1$s.
const format = (template, ...values) => {
    let next = 0;
    return template.replace(/%(?:(\d+)\$)?[sd]/g, (match, position) => String(values[position ? Number(position) - 1 : next++]));
};

const initBlock = (app) => {
    const endpoint = app.dataset.txpcatRest;
    const token    = app.dataset.txpcatCfg;
    const key      = app.dataset.txpcatKey || '';
    const results  = app.querySelector('[data-txpcat-results]');
    if (!endpoint || !token || !results) return;

    let strings = {};
    try {
        strings = JSON.parse(app.dataset.txpcatI18n || '{}');
    } catch {
        strings = {};
    }
    const str = {
        selected: '%d selected',
        product:  'product',
        products: 'products',
        remove:   'Remove filter %1$s: %2$s',
        clearAll: 'Clear all',
        search:   'Search',
        ...strings,
    };
    const pagerLabel = app.dataset.txpcatPagerLabel || '';

    const consoleEl = app.querySelector('.tx-pcat__console');
    const barEl     = app.querySelector('.tx-pcat__bar');
    const filters   = [...app.querySelectorAll('[data-txpcat-f]')];
    const totalEl   = app.querySelector('[data-txpcat-total]');
    const chipsEl   = app.querySelector('[data-txpcat-chips]');
    const sortEl    = app.querySelector('[data-txpcat-sort]');
    const emptyEl   = app.querySelector('[data-txpcat-empty]');
    const errorEl   = app.querySelector('[data-txpcat-error]');
    const resetBtn  = app.querySelector('[data-txpcat-reset]');
    const qInput    = app.querySelector('[data-txpcat-q]');
    const qFilter   = qInput ? qInput.closest('[data-txpcat-f]') : null;
    const presets   = [...app.querySelectorAll('[data-txpcat-preset]')];

    const rangeWrap = app.querySelector('[data-txpcat-range]');
    const rmin      = app.querySelector('[data-txpcat-rmin]');
    const rmax      = app.querySelector('[data-txpcat-rmax]');
    let stops = [];
    try {
        stops = JSON.parse((rangeWrap && rangeWrap.dataset.txpcatStops) || '[]');
    } catch {
        stops = [];
    }
    const last     = Math.max(0, stops.length - 1);
    const hasRange = Boolean(rangeWrap && rmin && rmax && stops.length > 1);

    // The configured default — the option the server marked selected,
    // which browser form restoration never changes.
    const initialSort = sortEl
        ? ([...sortEl.options].find((o) => o.defaultSelected) || sortEl.options[0] || { value: '' }).value
        : '';

    let paged    = 1;
    let open     = null;
    let inflight = null;
    let timer    = 0;
    let pending  = false;

    const part     = (el, selector) => el.querySelector(selector);
    const options  = (f) => [...f.querySelectorAll('[data-txpcat-opt]')];
    const checked  = (f) => options(f).filter((input) => input.checked);
    const optLabel = (input) => {
        const label = input.closest('.tx-pcat__opt');
        const text  = label && label.querySelector('.tx-pcat__opt-label');
        return text ? text.textContent.trim() : input.value;
    };
    const rangeIdx  = () => [Number(rmin.value), Number(rmax.value)];
    const rangeOn   = () => hasRange && (rangeIdx()[0] > 0 || rangeIdx()[1] < last);
    const rangeText = () => {
        const [lo, hi] = rangeIdx();
        return lo === hi ? `${kpsi(stops[lo])} psi` : `${kpsi(stops[lo])}–${kpsi(stops[hi])} psi`;
    };
    const nearestIdx = (value) => stops.reduce(
        (best, stop, i) => (Math.abs(stop - value) < Math.abs(stops[best] - value) ? i : best),
        0,
    );
    const setNoun = (el, n) => {
        const noun = el && el.querySelector('[data-txpcat-noun]');
        if (noun) noun.textContent = n === 1 ? str.product : str.products;
    };

    /* ---------------------------------------------------------------
     * State <-> controls
     * ------------------------------------------------------------- */

    const readState = () => {
        const state = {
            q:     qInput ? qInput.value.trim() : '',
            prmin: null,
            prmax: null,
            attr:  {},
            cat:   [],
            brand: [],
            sort:  sortEl ? sortEl.value : '',
            paged,
        };
        if (rangeOn()) {
            [state.prmin, state.prmax] = rangeIdx().map((i) => stops[i]);
        }
        filters.forEach((f) => {
            const values = checked(f).map((input) => input.value);
            const type   = f.dataset.txpcatType;
            if (type === 'attribute') state.attr[f.dataset.txpcatAttr] = values;
            if (type === 'category') state.cat = values;
            if (type === 'brand') state.brand = values;
        });
        return state;
    };

    const applyState = (state) => {
        if (qInput) qInput.value = state.q || '';
        if (hasRange) {
            const lo = state.prmin == null ? 0 : nearestIdx(Number(state.prmin));
            const hi = state.prmax == null ? last : nearestIdx(Number(state.prmax));
            rmin.value = String(Math.min(lo, hi));
            rmax.value = String(Math.max(lo, hi));
        }
        filters.forEach((f) => {
            const type = f.dataset.txpcatType;
            if (!['attribute', 'category', 'brand'].includes(type)) return;
            let want = state.brand;
            if (type === 'attribute') want = (state.attr || {})[f.dataset.txpcatAttr];
            if (type === 'category') want = state.cat;
            const list = Array.isArray(want) ? want.map(String) : [];
            options(f).forEach((input) => {
                input.checked = list.includes(input.value);
            });
        });
        if (sortEl && [...sortEl.options].some((o) => o.value === state.sort)) {
            sortEl.value = state.sort;
        }
        paged = Math.max(1, Number(state.paged) || 1);
    };

    const isDefault = (state) => !state.q
        && state.prmin == null
        && !Object.values(state.attr || {}).some((v) => v && v.length)
        && !(state.cat || []).length
        && !(state.brand || []).length
        && (!state.sort || state.sort === initialSort)
        && (Number(state.paged) || 1) === 1;

    const toParams = (state) => {
        const p = new URLSearchParams();
        p.set('cfg', token);
        if (state.q) p.set('q', state.q);
        if (state.prmin !== null) {
            p.set('prmin', String(state.prmin));
            p.set('prmax', String(state.prmax));
        }
        Object.entries(state.attr).forEach(([slug, values]) => {
            values.forEach((v) => p.append(`attr[${slug}][]`, v));
        });
        state.cat.forEach((v) => p.append('cat[]', v));
        state.brand.forEach((v) => p.append('brand[]', v));
        if (state.sort) p.set('sort', state.sort);
        if (state.paged > 1) p.set('paged', String(state.paged));
        return p;
    };

    const remember = (state) => {
        try {
            const current = window.history.state && typeof window.history.state === 'object' ? window.history.state : {};
            window.history.replaceState({ ...current, txpcat: { ...(current.txpcat || {}), [key]: state } }, '');
        } catch {
            // History unavailable (sandboxed frame) — Back simply shows the defaults.
        }
    };

    /* ---------------------------------------------------------------
     * Painting
     * ------------------------------------------------------------- */

    const paintRange = () => {
        if (!hasRange) return;
        const [lo, hi] = rangeIdx();
        const pct  = (i) => (last ? (i / last) * 100 : 0);
        const fill = part(rangeWrap, '[data-txpcat-range-fill]');
        if (fill) {
            fill.style.left  = `${pct(lo)}%`;
            fill.style.width = `${pct(hi) - pct(lo)}%`;
        }
        const minOut = app.querySelector('[data-txpcat-rmin-out]');
        const maxOut = app.querySelector('[data-txpcat-rmax-out]');
        if (minOut) minOut.textContent = psi(stops[lo]);
        if (maxOut) maxOut.textContent = psi(stops[hi]);
        rmin.setAttribute('aria-valuetext', `${psi(stops[lo])} psi`);
        rmax.setAttribute('aria-valuetext', `${psi(stops[hi])} psi`);
        // Only one thumb can be on top. Near the top end lift the min
        // thumb, so it can still be dragged down; when the thumbs sit
        // together, lift the one with room to move. The other one is
        // reachable with a press on the track.
        const liftMin = lo === hi ? lo > last / 2 : lo > last - 2;
        rmin.style.zIndex = liftMin ? '3' : '2';
    };

    const paintFilter = (f) => {
        const type = f.dataset.txpcatType;
        if (type === 'search') {
            f.classList.toggle('is-active', Boolean(qInput && qInput.value.trim()));
            return;
        }
        let n    = 0;
        let text = f.dataset.txpcatAll || '';
        if (type === 'pressure') {
            if (rangeOn()) {
                n    = 1;
                text = rangeText();
            }
        } else {
            const on = checked(f);
            n = on.length;
            if (n === 1) text = optLabel(on[0]);
            if (n > 1) text = format(str.selected, n);
        }
        f.classList.toggle('is-active', n > 0);
        const summary = part(f, '[data-txpcat-summary]');
        const badge   = part(f, '[data-txpcat-badge]');
        const clear   = part(f, '[data-txpcat-clear]');
        if (summary) summary.textContent = text;
        if (badge) {
            badge.textContent = String(n);
            badge.hidden      = n === 0;
        }
        if (clear) clear.hidden = n === 0;
    };

    const paintPresets = () => {
        const [lo, hi] = hasRange ? rangeIdx() : [0, last];
        presets.forEach((button) => {
            const on = stops[lo] === Number(button.dataset.min) && stops[hi] === Number(button.dataset.max);
            button.setAttribute('aria-pressed', on ? 'true' : 'false');
            if (on) button.disabled = false;
        });
    };

    const chipList = () => {
        const list = [];
        if (qInput && qInput.value.trim()) {
            list.push({
                group: qFilter ? qFilter.dataset.txpcatChip : str.search,
                value: `“${qInput.value.trim()}”`,
                clear: () => { qInput.value = ''; },
            });
        }
        filters.forEach((f) => {
            const type = f.dataset.txpcatType;
            if (type === 'pressure' && rangeOn()) {
                list.push({
                    group: f.dataset.txpcatChip,
                    value: rangeText(),
                    clear: () => {
                        rmin.value = '0';
                        rmax.value = String(last);
                    },
                });
            }
            if (['attribute', 'category', 'brand'].includes(type)) {
                checked(f).forEach((input) => {
                    list.push({
                        group: f.dataset.txpcatChip,
                        value: optLabel(input),
                        clear: () => { input.checked = false; },
                    });
                });
            }
        });
        return list;
    };

    const paintChips = () => {
        const list = chipList();
        if (resetBtn) resetBtn.hidden = list.length === 0;
        if (!chipsEl) return;

        const buttons = list.map((chip, i) => {
            const button = document.createElement('button');
            button.type      = 'button';
            button.className = 'mono tx-pcat__tag';
            button.setAttribute('aria-label', format(str.remove, chip.group, chip.value));
            const group = document.createElement('span');
            group.className   = 'tx-pcat__tag-k';
            group.textContent = chip.group;
            const x = document.createElement('span');
            x.setAttribute('aria-hidden', 'true');
            x.textContent = '×';
            button.append(group, document.createTextNode(` ${chip.value} `), x);
            button.addEventListener('click', () => {
                chip.clear();
                changed();
                // The chip is gone — keep keyboard focus in the row.
                const left = chipsEl.querySelectorAll('.tx-pcat__tag');
                const next = left[Math.min(i, left.length - 1)] || sortEl || results;
                next.focus();
            });
            return button;
        });

        if (list.length > 1) {
            const all = document.createElement('button');
            all.type        = 'button';
            all.className   = 'mono tx-pcat__link tx-pcat__clear-all';
            all.textContent = str.clearAll;
            all.addEventListener('click', () => {
                resetAll();
                (sortEl || results).focus();
            });
            buttons.push(all);
        }

        chipsEl.replaceChildren(...buttons);
        chipsEl.hidden = list.length === 0;
    };

    const paint = () => {
        paintRange();
        filters.forEach(paintFilter);
        paintPresets();
        paintChips();
    };

    const applyCounts = (counts) => {
        if (!counts) return;
        filters.forEach((f) => {
            const map = counts[f.dataset.txpcatF];
            if (!map) return;
            options(f).forEach((input) => {
                const n     = Number(map[input.value] || 0);
                const label = input.closest('.tx-pcat__opt');
                const out   = label && label.querySelector('[data-txpcat-n]');
                if (out) out.textContent = String(n);
                setNoun(label, n);
                // Never disable the control under the visitor's focus.
                if (document.activeElement !== input) input.disabled = n === 0 && !input.checked;
            });
        });
        presets.forEach((button) => {
            const n   = Number((counts.presets || [])[Number(button.dataset.txpcatPreset)] || 0);
            const out = part(button, '[data-txpcat-pn]');
            if (out) out.textContent = String(n);
            setNoun(button, n);
            if (document.activeElement !== button) {
                button.disabled = n === 0 && button.getAttribute('aria-pressed') !== 'true';
            }
        });
    };

    /* ---------------------------------------------------------------
     * Fetching
     * ------------------------------------------------------------- */

    const toResults = () => {
        const target = part(app, '.tx-pcat__toolbar') || results;
        const top    = target.getBoundingClientRect().top + window.scrollY - 16;
        if (top < window.scrollY) {
            window.scrollTo({ top, behavior: reducedMotion() ? 'auto' : 'smooth' });
        }
        // Only when the swap took the focused pager button with it — a
        // visitor who has moved on keeps their focus.
        const active = document.activeElement;
        if (!active || active === document.body || results.contains(active)) {
            results.focus({ preventScroll: true });
        }
    };

    const refresh = async ({ scroll = false, reconcile = false } = {}) => {
        window.clearTimeout(timer);
        pending = false;
        if (inflight) inflight.abort();
        const ctrl = new AbortController();
        inflight = ctrl;
        // A stalled connection must not leave the results dimmed and
        // dead; time out into the error path.
        const timeout = window.setTimeout(() => ctrl.abort(new DOMException('Request timed out', 'TimeoutError')), FETCH_TIMEOUT);

        const state = readState();
        app.classList.add('is-loading');
        results.setAttribute('aria-busy', 'true');
        try {
            const sep = endpoint.includes('?') ? '&' : '?';
            const res = await fetch(`${endpoint}${sep}${toParams(state)}`, {
                signal:  ctrl.signal,
                headers: { Accept: 'application/json' },
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();

            results.innerHTML = data.html || '';
            if (data.label) results.setAttribute('aria-label', data.label);
            const nav = part(results, 'nav.tx-pcat__pager');
            if (nav && pagerLabel) nav.setAttribute('aria-label', pagerLabel);
            if (errorEl) errorEl.hidden = true;
            if (totalEl) totalEl.textContent = String(data.total);
            if (emptyEl) emptyEl.hidden = data.total > 0;
            if (reconcile && data.state) {
                // A restored state may name options the scope no longer
                // offers; the server dropped them — mirror that.
                applyState(data.state);
                paint();
            }
            paged = Number(data.paged) || 1;
            applyCounts(data.counts);
            // New counts can re-wrap labels: re-measure the open list.
            if (open) placePanel(open);
            remember({ ...readState(), paged });
            if (scroll) toResults();
        } catch (err) {
            if (err.name !== 'AbortError') {
                // Keep the last results, but say they no longer match
                // the filters (e.g. a cached page whose config token the
                // server no longer accepts).
                if (errorEl) errorEl.hidden = false;
                console.error('tx-product-catalog:', err); // eslint-disable-line no-console
            }
        } finally {
            window.clearTimeout(timeout);
            // Only the still-current request may clear the shared state —
            // an aborted one settles after its replacement started.
            if (inflight === ctrl) {
                inflight = null;
                app.classList.remove('is-loading');
                results.removeAttribute('aria-busy');
            }
        }
    };

    const changed = (delay = 0) => {
        paged = 1;
        paint();
        // A response for the state before this edit must never land.
        if (inflight) inflight.abort();
        window.clearTimeout(timer);
        pending = true;
        timer   = window.setTimeout(() => refresh(), delay);
    };

    const resetAll = () => {
        if (qInput) qInput.value = '';
        if (hasRange) {
            rmin.value = '0';
            rmax.value = String(last);
        }
        filters.forEach((f) => options(f).forEach((input) => { input.checked = false; }));
        changed();
    };

    /* ---------------------------------------------------------------
     * Dropdown panels
     * ------------------------------------------------------------- */

    const triggerOf = (f) => part(f, '.tx-pcat__trigger');
    const panelOf   = (f) => part(f, '[data-txpcat-panel]');

    // As in the design, panels of items past the bar's middle open
    // toward the centre (right-aligned to their item); every panel is
    // then clamped inside the console, so none can leave the screen.
    const placePanel = (f) => {
        const panel = panelOf(f);
        if (!panel || panel.hidden) return;
        panel.style.left = '0px';
        const bounds = (consoleEl || app).getBoundingClientRect();
        const minX   = Math.max(bounds.left, 0);
        const maxX   = Math.min(bounds.right, document.documentElement.clientWidth);
        const item   = f.getBoundingClientRect();
        const bar    = (barEl || app).getBoundingClientRect();
        const width  = panel.getBoundingClientRect().width;
        const toLeft = item.left + item.width / 2 > bar.left + bar.width / 2;
        const left   = Math.max(minX, Math.min(toLeft ? item.right - width : item.left, maxX - width));
        panel.style.left = `${Math.round(left - item.left)}px`;
        const body = part(panel, '.tx-pcat__panel-body');
        if (body) body.classList.toggle('is-scrollable', body.scrollHeight > body.clientHeight);
    };

    const closePanel = (f, focusTrigger = false) => {
        const panel = panelOf(f);
        if (panel) panel.hidden = true;
        f.classList.remove('is-open');
        const trigger = triggerOf(f);
        if (trigger) {
            trigger.setAttribute('aria-expanded', 'false');
            if (focusTrigger) trigger.focus();
        }
        if (open === f) open = null;
    };

    const openPanel = (f) => {
        if (open && open !== f) closePanel(open);
        const panel = panelOf(f);
        if (!panel) return;
        panel.hidden = false;
        f.classList.add('is-open');
        triggerOf(f).setAttribute('aria-expanded', 'true');
        open = f;
        placePanel(f);
        // Short screens (landscape phones): bring DONE into view.
        if (panel.getBoundingClientRect().bottom > window.innerHeight) {
            panel.scrollIntoView({ block: 'nearest', behavior: reducedMotion() ? 'auto' : 'smooth' });
        }
    };

    /* ---------------------------------------------------------------
     * Wiring
     * ------------------------------------------------------------- */

    filters.forEach((f) => {
        const trigger = triggerOf(f);
        if (trigger) {
            trigger.addEventListener('click', () => (open === f ? closePanel(f) : openPanel(f)));
        }
        const done = part(f, '[data-txpcat-done]');
        if (done) done.addEventListener('click', () => closePanel(f, true));
        const clear = part(f, '[data-txpcat-clear]');
        if (clear) {
            clear.addEventListener('click', () => {
                if (f.dataset.txpcatType === 'pressure') {
                    rmin.value = '0';
                    rmax.value = String(last);
                } else {
                    options(f).forEach((input) => { input.checked = false; });
                }
                changed();
                // CLEAR hides itself once nothing is selected.
                if (done) done.focus();
            });
        }
        f.addEventListener('change', (e) => {
            if (e.target.matches('[data-txpcat-opt]')) changed();
        });
        // Tabbing out closes the panel. A press on a non-focusable part
        // of the panel hands focus to the nearest focusable ancestor
        // (<main tabindex="-1">) — that is not leaving; outside presses
        // are the document listeners' job.
        f.addEventListener('focusout', (e) => {
            const to = e.relatedTarget;
            if (open === f && to && !f.contains(to) && !to.contains(f)) closePanel(f);
        });
    });

    if (hasRange) {
        const onRange = (moved) => () => {
            const [lo, hi] = rangeIdx();
            if (lo > hi) {
                if (moved === rmin) rmin.value = String(hi);
                else rmax.value = String(lo);
            }
            changed(RANGE_DELAY);
        };
        rmin.addEventListener('input', onRange(rmin));
        rmax.addEventListener('input', onRange(rmax));

        // A press on the track moves the nearer thumb there — the
        // single-pointer alternative to dragging (WCAG 2.5.7). Releases
        // of a thumb drag also land here as a click; skip those.
        let fromThumb = false;
        rangeWrap.addEventListener('pointerdown', (e) => {
            fromThumb = e.target === rmin || e.target === rmax;
        });
        rangeWrap.addEventListener('click', (e) => {
            if (fromThumb || e.target === rmin || e.target === rmax) return;
            const box = rangeWrap.getBoundingClientRect();
            const i   = Math.max(0, Math.min(last, Math.round(((e.clientX - box.left) / box.width) * last)));
            const [lo, hi] = rangeIdx();
            let thumb = rmax;
            if (i <= lo || (i < hi && i - lo <= hi - i)) thumb = rmin;
            thumb.value = String(i);
            changed();
        });
    }

    presets.forEach((button) => {
        button.addEventListener('click', () => {
            const lo = stops.indexOf(Number(button.dataset.min));
            const hi = stops.indexOf(Number(button.dataset.max));
            if (!hasRange || lo < 0 || hi < 0) return;
            const on = button.getAttribute('aria-pressed') === 'true';
            rmin.value = String(on ? 0 : lo);
            rmax.value = String(on ? last : hi);
            changed();
        });
    });

    if (qInput) {
        qInput.addEventListener('input', () => changed(SEARCH_DELAY));
        qInput.addEventListener('keydown', (e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            changed();
            // Drop the on-screen keyboard so the results show.
            if (coarsePointer()) qInput.blur();
        });
        qInput.addEventListener('focus', () => {
            if (open) closePanel(open);
        });
    }

    const form = app.querySelector('[data-txpcat-form]');
    if (form) form.addEventListener('submit', (e) => e.preventDefault());

    if (sortEl) sortEl.addEventListener('change', () => changed());
    if (resetBtn) {
        resetBtn.addEventListener('click', () => {
            resetAll();
            (sortEl || results).focus();
        });
    }

    results.addEventListener('click', (e) => {
        const button = e.target.closest('[data-txpcat-page]');
        if (!button || button.disabled) return;
        // A filter change still waiting out its debounce wins: its
        // results start at page 1, and this pager belongs to the old ones.
        if (!pending) paged = Math.max(1, Number(button.dataset.txpcatPage) || 1);
        refresh({ scroll: true });
    });

    // Mouse and pen close an open panel on press outside it. Touch waits
    // for a tap — a pointerup that barely moved; a scroll ends in
    // pointercancel instead — and doesn't rely on iOS delivering click
    // events for taps on non-interactive content.
    let touchStart = null;
    document.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'touch') {
            touchStart = open && !open.contains(e.target) ? { x: e.clientX, y: e.clientY } : null;
            return;
        }
        if (open && !open.contains(e.target)) closePanel(open);
    });
    document.addEventListener('pointerup', (e) => {
        if (e.pointerType !== 'touch' || !touchStart || !open) return;
        const moved = Math.hypot(e.clientX - touchStart.x, e.clientY - touchStart.y);
        touchStart = null;
        if (moved < 10 && !open.contains(e.target)) closePanel(open);
    });
    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape' || !open) return;
        const active = document.activeElement;
        closePanel(open, !active || active === document.body || open.contains(active) || active.contains(open));
    });
    window.addEventListener('resize', () => {
        if (open) placePanel(open);
    }, { passive: true });

    paint();

    const saved = window.history.state && window.history.state.txpcat && window.history.state.txpcat[key];
    if (saved && !isDefault(saved)) {
        applyState(saved);
        paint();
        refresh({ reconcile: true });
    }

    // WebKit resets an autocomplete="off" form's controls just after a
    // bfcache restore, under results that kept their state: put the
    // remembered state back.
    window.addEventListener('pageshow', (e) => {
        if (!e.persisted) return;
        window.setTimeout(() => {
            const state = window.history.state && window.history.state.txpcat && window.history.state.txpcat[key];
            if (state) {
                applyState(state);
                paint();
            }
        }, 0);
    });
};

const init = () => {
    document.querySelectorAll('[data-txpcat]').forEach(initBlock);
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
